import {
  type CatalogDetailsInput,
  CatalogLookupResponseSchema,
  ClinicCatalogSubmissionSchema,
  ClinicDefaultItemListResponseSchema,
  FefoResponseSchema,
  type InventoryItemDetail,
  InventoryItemDetailSchema,
  type InventoryItemInput,
  InventoryItemListResponseSchema,
  InventoryAlertsSchema,
  type InventorySettings,
  InventorySettingsSchema,
  PendingCostListResponseSchema,
  type ReceiptCostsInput,
  type ReceiptInput,
  ReceiptListResponseSchema,
  ReceiptSchema,
  type StockAdjustInput,
  StockMovementListResponseSchema,
  type StockWriteOffInput,
  TraceResponseSchema,
} from '@clinic/contracts';
import { keepPreviousData, createInfiniteQuery, createQuery, useQueryClient } from '@tanstack/svelte-query';
import { createMutation } from '../../../shared/api/mutation';
import { apiRequest } from '../../../shared/api/client';
import type { z } from 'zod';
import { type MaybeGetter, read } from '../../../shared/lib/maybe';
import { keys } from '../../../shared/api/keys';

const base = '/v1/clinic/inventory';

export function useInventoryItems(
  orgId: string,
  filter: MaybeGetter<{ q?: string; status?: 'active' | 'archived'; gtin?: string }>,
  enabled: MaybeGetter<boolean> = true,
) {
  return createInfiniteQuery(() => {
    const f = read(filter);
    const params = new URLSearchParams({ limit: '50', status: f.status ?? 'active' });
    if (f.q) params.set('q', f.q);
    if (f.gtin) params.set('gtin', f.gtin);
    return {
      queryKey: [...keys.inventory(orgId), 'items', params.toString()],
      queryFn: ({ signal, pageParam }: { signal: AbortSignal; pageParam: string }) => {
        const page = new URLSearchParams(params);
        if (pageParam) page.set('cursor', pageParam);
        return apiRequest(`${base}/items?${page.toString()}`, { orgId, schema: InventoryItemListResponseSchema, signal });
      },
      initialPageParam: '',
      getNextPageParam: (last: z.infer<typeof InventoryItemListResponseSchema>) => last.nextCursor ?? undefined,
      placeholderData: keepPreviousData,
      enabled: read(enabled),
    };
  });
}

/** The item a scanned product barcode belongs to, if the clinic has linked it (GTIN is unique). */
export async function findItemByGtin(orgId: string, gtin: string) {
  const page = await apiRequest(`${base}/items?gtin=${encodeURIComponent(gtin)}&limit=1`, {
    orgId,
    schema: InventoryItemListResponseSchema,
  });
  return page.items[0] ?? null;
}

export function useInventoryItem(orgId: string, itemId: MaybeGetter<string | null>) {
  return createQuery(() => ({
    queryKey: [...keys.inventory(orgId), 'item', read(itemId)],
    queryFn: ({ signal }) => apiRequest(`${base}/items/${read(itemId) ?? ''}`, { orgId, schema: InventoryItemDetailSchema, signal }),
    enabled: read(itemId) !== null,
  }));
}

export function useItemMovements(orgId: string, itemId: MaybeGetter<string | null>) {
  return createInfiniteQuery(() => ({
    queryKey: [...keys.inventory(orgId), 'movements', read(itemId)],
    queryFn: ({ signal, pageParam }) =>
      apiRequest(`${base}/items/${read(itemId) ?? ''}/movements?limit=30${pageParam ? `&cursor=${pageParam}` : ''}`, {
        orgId,
        schema: StockMovementListResponseSchema,
        signal,
      }),
    initialPageParam: '',
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    enabled: read(itemId) !== null,
  }));
}

/** Writes answer with the item's new detail; every stock list may have changed. */
function useInventoryWrite<V>(orgId: string, fn: (vars: V) => Promise<InventoryItemDetail>) {
  const qc = useQueryClient();
  return createMutation(() => ({
    mutationFn: fn,
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.inventory(orgId) }),
  }));
}

export function useCreateItem(orgId: string) {
  return useInventoryWrite(orgId, (body: InventoryItemInput) =>
    apiRequest(`${base}/items`, { method: 'POST', body, orgId, schema: InventoryItemDetailSchema }),
  );
}

export function useUpdateItem(orgId: string, itemId: MaybeGetter<string>) {
  return useInventoryWrite(orgId, (body: InventoryItemInput) =>
    apiRequest(`${base}/items/${read(itemId)}`, { method: 'PUT', body, orgId, schema: InventoryItemDetailSchema }),
  );
}

export function useArchiveItem(orgId: string, itemId: MaybeGetter<string>) {
  return useInventoryWrite(orgId, (archived: boolean) =>
    apiRequest(`${base}/items/${read(itemId)}/${archived ? 'archive' : 'restore'}`, {
      method: 'POST',
      orgId,
      schema: InventoryItemDetailSchema,
    }),
  );
}

/** `key` is chosen once per correction the user makes and resent if they retry it. */
export function useAdjustBatch(orgId: string) {
  return useInventoryWrite(orgId, ({ batchId, key, body }: { batchId: string; key: string; body: StockAdjustInput }) =>
    apiRequest(`${base}/batches/${batchId}/adjust`, {
      method: 'POST',
      body,
      orgId,
      idempotencyKey: key,
      schema: InventoryItemDetailSchema,
    }),
  );
}

export function useWriteOffBatch(orgId: string) {
  return useInventoryWrite(orgId, ({ batchId, key, body }: { batchId: string; key: string; body: StockWriteOffInput }) =>
    apiRequest(`${base}/batches/${batchId}/write-off`, {
      method: 'POST',
      body,
      orgId,
      idempotencyKey: key,
      schema: InventoryItemDetailSchema,
    }),
  );
}

export function useReleaseReservation(orgId: string) {
  return useInventoryWrite(orgId, (batchId: string) =>
    apiRequest(`${base}/batches/${batchId}/release-reservation`, {
      method: 'POST',
      orgId,
      schema: InventoryItemDetailSchema,
    }),
  );
}

export function useReceipts(orgId: string) {
  return createInfiniteQuery(() => ({
    queryKey: [...keys.inventory(orgId), 'receipts'],
    queryFn: ({ signal, pageParam }) =>
      apiRequest(`${base}/receipts?limit=20${pageParam ? `&cursor=${pageParam}` : ''}`, {
        orgId,
        schema: ReceiptListResponseSchema,
        signal,
      }),
    initialPageParam: '',
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  }));
}

export function useReceive(orgId: string) {
  const qc = useQueryClient();
  return createMutation(() => ({
    mutationFn: ({ key, body }: { key: string; body: ReceiptInput }) =>
      apiRequest(`${base}/receipts`, { method: 'POST', body, orgId, idempotencyKey: key, schema: ReceiptSchema }),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.inventory(orgId) }),
  }));
}

/** Receipt lines without a purchase price (ADR-053), for members with inventory.cost. */
export function usePendingCosts(orgId: string, enabled: MaybeGetter<boolean> = true) {
  return createQuery(() => ({
    queryKey: [...keys.inventory(orgId), 'pending-costs'],
    queryFn: ({ signal }) => apiRequest(`${base}/pending-costs`, { orgId, schema: PendingCostListResponseSchema, signal }),
    enabled: read(enabled),
  }));
}

/** Prices for lines of one receipt; the books get the purchase. A line priced meanwhile by someone
 * else answers RECEIPT_COST_RECORDED: the list is read again either way. */
export function useRecordCosts(orgId: string) {
  const qc = useQueryClient();
  return createMutation(() => ({
    mutationFn: ({ receiptId, body }: { receiptId: string; body: ReceiptCostsInput }) =>
      apiRequest(`${base}/receipts/${receiptId}/costs`, { method: 'POST', body, orgId, schema: ReceiptSchema }),
    onSettled: () =>
      Promise.all([qc.invalidateQueries({ queryKey: keys.inventory(orgId) }), qc.invalidateQueries({ queryKey: keys.finance(orgId) })]),
  }));
}

export function useInventoryAlerts(orgId: string, enabled: MaybeGetter<boolean> = true) {
  return createQuery(() => ({
    queryKey: [...keys.inventory(orgId), 'alerts'],
    queryFn: ({ signal }) => apiRequest(`${base}/alerts`, { orgId, schema: InventoryAlertsSchema, signal }),
    enabled: read(enabled),
    // Expiry moves with the calendar, not only with changes.
    refetchInterval: 30 * 60_000,
  }));
}

export function useTrace(orgId: string, query: MaybeGetter<{ itemId: string; lot: string } | { uid: string } | null>) {
  return createQuery(() => {
    const q = read(query);
    const params = q ? new URLSearchParams('uid' in q ? { uid: q.uid } : q).toString() : '';
    return {
      queryKey: [...keys.inventory(orgId), 'trace', params],
      queryFn: ({ signal }: { signal: AbortSignal }) =>
        apiRequest(`${base}/trace?${params}`, { orgId, schema: TraceResponseSchema, signal }),
      enabled: q !== null,
    };
  });
}

export function useInventorySettings(orgId: string) {
  return createQuery(() => ({
    queryKey: [...keys.inventory(orgId), 'settings'],
    queryFn: ({ signal }) => apiRequest(`${base}/settings`, { orgId, schema: InventorySettingsSchema, signal }),
    staleTime: 5 * 60_000,
  }));
}

export function useUpdateInventorySettings(orgId: string) {
  const qc = useQueryClient();
  return createMutation(() => ({
    mutationFn: (body: InventorySettings) =>
      apiRequest(`${base}/settings`, { method: 'PUT', body, orgId, schema: InventorySettingsSchema }),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.inventory(orgId) }),
  }));
}

/** Usable batches of an item for one patient, best first (BR-05); the server orders them. */
export function useFefo(orgId: string, itemId: MaybeGetter<string | null>, patientId: MaybeGetter<string>) {
  return createQuery(() => ({
    queryKey: [...keys.inventory(orgId), 'fefo', read(itemId), read(patientId)],
    queryFn: ({ signal }) =>
      apiRequest(`${base}/fefo?itemId=${read(itemId) ?? ''}&patientId=${read(patientId)}`, { orgId, schema: FefoResponseSchema, signal }),
    enabled: read(itemId) !== null,
  }));
}

/**
 * The approved catalog product of a scanned GTIN, and this clinic's own proposal of it (ADR-055).
 * It only prefills a new item's form; an existing item is never changed by it.
 */
export function lookupCatalog(orgId: string, gtin: string) {
  return apiRequest(`/v1/clinic/catalog/lookup?gtin=${encodeURIComponent(gtin)}`, { orgId, schema: CatalogLookupResponseSchema });
}

/** Adds details (brand, model, content, pack) to this clinic's pending proposal of a GTIN. */
export function useSaveCatalogDetails(orgId: string) {
  const qc = useQueryClient();
  return createMutation(() => ({
    mutationFn: ({ gtin, body }: { gtin: string; body: CatalogDetailsInput }) =>
      apiRequest(`/v1/clinic/catalog/submissions/${gtin}`, { method: 'PUT', body, orgId, schema: ClinicCatalogSubmissionSchema }),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.inventory(orgId) }),
  }));
}

/** Platform-managed default item list for prefilling a new clinic item form (ADR-057). */
export function useDefaultItems(orgId: string, enabled: MaybeGetter<boolean> = true) {
  return createQuery(() => ({
    queryKey: [...keys.inventory(orgId), 'default-items'],
    queryFn: ({ signal }) => apiRequest('/v1/clinic/catalog/default-items', { orgId, schema: ClinicDefaultItemListResponseSchema, signal }),
    enabled: read(enabled),
    staleTime: 5 * 60_000,
  }));
}
